import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';

/**
 * Sprites de footsteps (huellas al caminar).
 *
 * Un único sheet (`footsteps_1.png`) de 12 columnas × 8 filas, frame 48×48,
 * organizado en 8 "sets" de 3 columnas × 4 filas (4 sets en la banda superior,
 * 4 en la inferior). Cada set tiene un MODO:
 *
 *   - Sets 1–4 → DIRECCIONAL: la FILA indica la dirección de la pisada y las 3
 *     COLUMNAS son la animación de aparición de UNA huella. Persisten en el piso
 *     y se desvanecen según el stat `footstepDuration` de la entidad.
 *
 *   - Sets 5–7 → ANIMACIÓN: los 12 frames del set (row-major, izq→der fila por
 *     fila) son UNA sola animación (ej. splash de agua). Se reproducen enteros
 *     una vez por paso y se destruyen al terminar (no persisten, ignoran el stat).
 *
 *   - Set 8 → VACÍO (sin huella).
 *
 * Orden de filas dentro de un set direccional (propio de este asset):
 *   row0 = DOWN, row1 = LEFT, row2 = RIGHT, row3 = UP
 *
 * ⚠️ Ese orden NO coincide con el del enum `Direction` (`UP=0,RIGHT=1,DOWN=2,LEFT=3`)
 * ni con el de body/head/boot, por eso se mapea explícitamente en `FOOTSTEP_DIR_ROW`.
 */

export const FOOTSTEP_SHEET_KEY = 'footsteps';
export const FOOTSTEP_SHEET_PATH = '/assets/world/footsteps/footsteps_1.png';

export const FOOTSTEP_COLS = 12;
export const FOOTSTEP_ROWS = 8;
export const FOOTSTEP_FRAME = 48;

/** Cantidad de sets (footsteps) en el sheet. */
export const FOOTSTEP_SETS = 8;
/** Columnas/filas de cada set individual. */
const SET_COLS = 3;
const SET_ROWS = 4;
/** Cuántos sets entran por banda horizontal (12 / 3). */
const SETS_PER_BAND = FOOTSTEP_COLS / SET_COLS; // 4

/** Footstep por defecto del jugador y de los NPCs (arriba-izquierda del sheet). */
export const DEFAULT_PLAYER_FOOTSTEP_ID = 1;
/** Footstep usado al pisar la costa (borde del agua): splash azul. */
export const COAST_FOOTSTEP_ID = 5;

export type FootstepMode = 'directional' | 'animation' | 'empty';

/** Modo de cada set. Sets 1–4 direccionales, 5–7 animación, 8 vacío. */
const FOOTSTEP_SET_MODE: Record<number, FootstepMode> = {
  1: 'directional',
  2: 'directional',
  3: 'directional',
  4: 'directional',
  5: 'animation',
  6: 'animation',
  7: 'animation',
  8: 'empty',
};

/**
 * Dirección del enum → fila dentro del set (solo sets direccionales).
 * Asset: row0=DOWN, row1=LEFT, row2=RIGHT, row3=UP.
 */
const FOOTSTEP_DIR_ROW: Record<Direction, number> = {
  [Direction.DOWN]: 0,
  [Direction.LEFT]: 1,
  [Direction.RIGHT]: 2,
  [Direction.UP]: 3,
};

/**
 * Carga el sheet de footsteps. 576/12=48 y 384/8=48 son múltiplos exactos, así
 * que un spritesheet uniforme corta limpio (sin drift sub-pixel).
 */
export function preloadFootsteps(scene: Phaser.Scene): void {
  if (scene.textures.exists(FOOTSTEP_SHEET_KEY)) return;
  scene.load.spritesheet(FOOTSTEP_SHEET_KEY, FOOTSTEP_SHEET_PATH, {
    frameWidth: FOOTSTEP_FRAME,
    frameHeight: FOOTSTEP_FRAME,
  });
}

export function getFootstepMode(footstepId: number): FootstepMode {
  return FOOTSTEP_SET_MODE[footstepId] ?? 'empty';
}

/** true si el set no tiene arte (no hay que dibujar huella). */
export function isFootstepSetEmpty(footstepId: number): boolean {
  return getFootstepMode(footstepId) === 'empty';
}

/** Fila/columna base (row-major) de la celda origen de un set (0-based). */
function footstepSetOrigin(footstepId: number): { baseRow: number; baseCol: number } {
  const s = footstepId - 1;
  const bandRow = Math.floor(s / SETS_PER_BAND); // 0 = banda superior, 1 = inferior
  const bandCol = s % SETS_PER_BAND;
  return { baseRow: bandRow * SET_ROWS, baseCol: bandCol * SET_COLS };
}

/**
 * Índices de frame (row-major, 0..95) de las 3 columnas de animación de un
 * footstep DIRECCIONAL para una dirección dada.
 */
export function getDirectionalFootstepFrames(footstepId: number, dir: Direction): number[] {
  const { baseRow, baseCol } = footstepSetOrigin(footstepId);
  const row = baseRow + FOOTSTEP_DIR_ROW[dir];
  return [0, 1, 2].map((c) => row * FOOTSTEP_COLS + baseCol + c);
}

/**
 * Índices de frame (row-major) de los 12 frames de un footstep de ANIMACIÓN,
 * leídos izq→der fila por fila (3 cols × 4 rows).
 */
export function getAnimationFootstepFrames(footstepId: number): number[] {
  const { baseRow, baseCol } = footstepSetOrigin(footstepId);
  const frames: number[] = [];
  for (let r = 0; r < SET_ROWS; r++) {
    for (let c = 0; c < SET_COLS; c++) {
      frames.push((baseRow + r) * FOOTSTEP_COLS + baseCol + c);
    }
  }
  return frames;
}

/** Key de la animación de Phaser para un footstep direccional (id + dirección). */
export function getDirectionalFootstepAnimKey(footstepId: number, dir: Direction): string {
  return `footstep_${footstepId}_${dir}`;
}

/** Key de la animación de Phaser para un footstep de animación (id, sin dirección). */
export function getAnimationFootstepAnimKey(footstepId: number): string {
  return `footstep_${footstepId}`;
}

/**
 * Resuelve qué footstep usar según la superficie pisada. Hoy: costa → splash
 * (set 5); en cualquier otro caso, el footstep propio de la entidad.
 */
export function resolveFootstepId(entityDefaultId: number, isCoast: boolean): number {
  return isCoast ? COAST_FOOTSTEP_ID : entityDefaultId;
}
